import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, Filter } from "lucide-react";
import { SiteShell } from "@/project/components/site-shell";
import { InstitutionCard } from "@/project/components/cards";
import { institutions, institutionTypes, animalTypes } from "@/project/data/sample";

export const Route = createFileRoute("/institutions/")({
  head: () => ({
    meta: [
      { title: "Verified animal welfare institutions — Animal Seva" },
      { name: "description", content: "Browse verified Panjrapols, Gaushalas, animal shelters, bird hospitals and rescue centres across India." },
    ],
  }),
  component: InstitutionsPage,
});

function InstitutionsPage() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [animal, setAnimal] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const list = useMemo(() => {
    return institutions.filter((i) => {
      if (q && !(`${i.name} ${i.city} ${i.state}`.toLowerCase().includes(q.toLowerCase()))) return false;
      if (type && i.type !== type) return false;
      if (animal && !i.animals.includes(animal)) return false;
      if (verifiedOnly && !i.verified) return false;
      return true;
    });
  }, [q, type, animal, verifiedOnly]);

  return (
    <SiteShell>
      <section className="border-b border-border/60 bg-[var(--cream-2)]">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">Institutions</p>
          <h1 className="mt-2 font-display text-3xl font-semibold sm:text-4xl text-[var(--maroon-deep)]">
            Verified Panjrapols, Gaushalas & animal shelters
          </h1>
          <p className="mt-3 max-w-2xl text-foreground/70">
            Search by city, animal type or institution type. Every listing has passed our admin verification process.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="mb-8 grid gap-3 rounded-2xl border border-border/60 bg-card p-4 shadow-[var(--shadow-soft)] md:grid-cols-12">
          <div className="md:col-span-5 flex items-center gap-2 rounded-full border border-border bg-background px-3">
            <Search className="h-4 w-4 text-foreground/70" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or city…"
                   className="flex-1 bg-transparent py-2.5 text-sm outline-none" />
          </div>
          <select value={type} onChange={(e) => setType(e.target.value)} className="md:col-span-3 rounded-full border border-border bg-background px-4 py-2.5 text-sm">
            <option value="">All types</option>
            {institutionTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={animal} onChange={(e) => setAnimal(e.target.value)} className="md:col-span-3 rounded-full border border-border bg-background px-4 py-2.5 text-sm">
            <option value="">All animals</option>
            {animalTypes.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <label className="md:col-span-1 flex items-center justify-center gap-2 rounded-full border border-border px-3 text-xs">
            <input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} />
            Verified
          </label>
        </div>

        {list.length === 0 ? (
          <div className="rounded-2xl border border-border/60 bg-card p-10 text-center text-foreground/60">
            <Filter className="mx-auto h-6 w-6" />
            <p className="mt-2">No institutions match your filters.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {list.map((i) => <InstitutionCard key={i.id} inst={i} />)}
          </div>
        )}

        <div className="mt-12 rounded-2xl border border-dashed border-[var(--maroon)]/30 bg-[var(--cream-2)] p-6 text-center">
          <p className="font-display text-lg text-[var(--maroon-deep)]">Run a Panjrapol or shelter?</p>
          <p className="mt-1 text-sm text-foreground/70">List your institution and start receiving donations transparently.</p>
          <Link to="/register-institution" className="mt-4 inline-flex rounded-full bg-[var(--maroon)] px-5 py-2.5 text-sm font-semibold text-[var(--cream)]">
            Register your institution
          </Link>
        </div>
      </section>
    </SiteShell>
  );
}